import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Team", href: "#team" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-border/70 shadow-[0_1px_20px_rgba(15,23,42,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={SITE.logo} alt="Sagos Consulting" className="h-16 md:h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <button
              data-testid="nav-portal-btn"
              onClick={() => navigate("/portal")}
              className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#0b2a5b] transition-colors duration-200"
            >
              My Portal
            </button>
          ) : (
            <>
              <button
                data-testid="nav-login-btn"
                onClick={() => navigate("/login")}
                className="text-sm font-medium text-slate-700 hover:text-primary transition-colors duration-200"
              >
                Client Login
              </button>
              <button
                data-testid="nav-consult-btn"
                onClick={() => navigate("/register")}
                className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#0b2a5b] transition-colors duration-200"
              >
                Become a Client
              </button>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-slate-800"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-border px-5 py-5 space-y-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-slate-700 font-medium"
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => navigate("/login")}
              className="flex-1 py-2.5 rounded-full border border-border text-sm font-medium"
            >
              Login
            </button>
            <button
              onClick={() => navigate("/register")}
              className="flex-1 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
